import { useCallback, useMemo, useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Select,
  type SelectChangeEvent,
} from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";

import { getSelectableEnvs, type CrownEnv } from "./env-config";
import { useCrownEnv } from "./env-context";
import { envSwitchAction, needsConfirmation } from "./env-switch";

const styles = {
  select: {
    width: "100%",
  },
  option: {},
  prodOption: {
    color: "error.main",
    fontWeight: 600,
  },
} satisfies Record<string, SxProps<Theme>>;

/**
 * Dropdown that selects the Crown target environment.
 *
 * Reads and writes the active env through `useCrownEnv`, so the choice is
 * shared across the whole tool and persisted by the provider.
 *
 * Switching into prod is confirmed first. Every module reached from here writes
 * to the selected env, and the switch itself was a single uncommitted click, so
 * a mis-click while hunting for demo pointed bulk import, config migration and
 * piece backfill at live customer data with nothing to undo it.
 */
const EnvPicker = () => {
  const { env, setEnv } = useCrownEnv();
  const [pendingEnv, setPendingEnv] = useState<CrownEnv | null>(null);

  // REACT_APP_* URLs are build-time constants, so the selectable set never
  // changes at runtime — memoize with empty deps for a stable reference.
  const selectableEnvs = useMemo(() => getSelectableEnvs(), []);

  const handleChange = useCallback(
    (event: SelectChangeEvent<CrownEnv>) => {
      const next = event.target.value as CrownEnv;
      const action = envSwitchAction(env, next);
      if (action === "ignore") {
        return;
      }
      if (action === "confirm") {
        setPendingEnv(next);
        return;
      }
      setEnv(next);
    },
    [env, setEnv],
  );

  const handleCancel = useCallback(() => {
    setPendingEnv(null);
  }, []);

  const handleConfirm = useCallback(() => {
    if (pendingEnv !== null) {
      setEnv(pendingEnv);
    }
    setPendingEnv(null);
  }, [pendingEnv, setEnv]);

  return (
    <>
      <Select<CrownEnv>
        size="small"
        value={env}
        data-testid="crown-env-picker"
        sx={styles.select}
        onChange={handleChange}
      >
        {selectableEnvs.map((value) => (
          <MenuItem
            key={value}
            value={value}
            sx={needsConfirmation(value) ? styles.prodOption : styles.option}
          >
            {value.toUpperCase()}
          </MenuItem>
        ))}
      </Select>

      <Dialog open={pendingEnv !== null} onClose={handleCancel}>
        <DialogTitle>Switch to production?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Every tool will read and write live customer data until you switch
            back. Imports, config migration and piece backfill cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            data-testid="crown-env-confirm-prod"
            onClick={handleConfirm}
          >
            Switch to prod
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EnvPicker;
